import { useEffect } from 'react';
import { getCapInformation } from 'components/ServiceAuthorization/utils';
import dollarsToCents from 'common/utils/Money/dollarsToCents';
import { useAuthorizedSpendsWithExclusions } from 'pages/service-authorizations/hooks/useAuthorizedSpendsWithExclusions';

function useAuthorizationCapUtilization({
  feeScheduleProgram,
  person,
  isUnitBased,
  requestedStartsAt,
  requestedEndsAt,
  authorizedAmount,
  setIsAmountExceeded,
  shouldExcludeServiceAuthorizations = false,
  authorization,
}) {
  const excludeAuthorizationId = authorization?.original_service_authorization?.id || authorization?.id;

  const exclusionFilters = shouldExcludeServiceAuthorizations && {
    original_and_editions: { id: excludeAuthorizationId },
    non_original_from_requested: true,
  };

  const baseParams = {
    feeScheduleProgram: feeScheduleProgram.id,
    person: person.id,
    requestedStartsAt: requestedStartsAt.toISOString(),
    requestedEndsAt: requestedEndsAt.toISOString(),
    enabled: requestedEndsAt &&
      requestedStartsAt &&
      requestedEndsAt.isSameOrAfter(requestedStartsAt) &&
      Boolean(feeScheduleProgram.id),
  };

  const {
    authorizedSpends,
    meta,
    isFetching,
  } = useAuthorizedSpendsWithExclusions(baseParams, exclusionFilters);

  const totalApprovedSpend = meta?.total_approved_spend || 0;
  const totalRequestedSpend = meta?.total_requested_spend || 0;
  const availableCap = meta?.available_cap || 0;
  const capInfo = getCapInformation(feeScheduleProgram, meta);

  const parsedAuthorizedAmount = isUnitBased ?
    parseInt(authorizedAmount, 10) : dollarsToCents(authorizedAmount);

  const isAmountExceeded = capInfo.hasCap && !!parsedAuthorizedAmount &&
    (parsedAuthorizedAmount > availableCap);

  useEffect(() => {
    setIsAmountExceeded(capInfo.hasCap && isAmountExceeded);
    return () => {
      setIsAmountExceeded(false);
    };
  }, [isAmountExceeded, capInfo.hasCap, setIsAmountExceeded]);

  return {
    isFetching,
    capInfo,
    availableCap,
    authorizedSpends,
    totalApprovedSpend,
    totalRequestedSpend,
  };
}

export default useAuthorizationCapUtilization;
